/**
 * GitLab connection and project API helpers (org-scoped).
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.druploy.dev';

export interface GitLabStatus {
  connected: boolean;
  gitlab_url: string;
}

export interface GitLabProject {
  id: number;
  name: string;
  path_with_namespace: string;
  description: string;
  web_url: string;
  default_branch: string;
  previews_enabled: boolean;
  webhook_active: boolean | null;
}

export async function getGitLabStatus(orgSlug: string): Promise<GitLabStatus> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/gitlab/status`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to get GitLab status');
  return resp.json();
}

export async function connectGitLab(orgSlug: string, gitlabUrl: string, token: string): Promise<{ success: boolean; gitlab_url: string; token_name: string }> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/gitlab/connect`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gitlab_url: gitlabUrl, token }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to connect GitLab');
  }
  return resp.json();
}

export async function getGitLabProjects(orgSlug: string): Promise<GitLabProject[]> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/gitlab/projects`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to list GitLab projects');
  const data = await resp.json();
  return data.projects;
}

export async function getEnabledProjects(orgSlug: string): Promise<GitLabProject[]> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/gitlab/projects/enabled`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to list enabled projects');
  const data = await resp.json();
  return data.projects;
}

export async function enableProjectPreviews(
  orgSlug: string,
  projectId: number,
  project: { path_with_namespace: string; name: string; web_url: string; default_branch: string },
): Promise<{ success: boolean; hook_id: number }> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/gitlab/projects/${projectId}/enable`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      path_with_namespace: project.path_with_namespace,
      name: project.name,
      web_url: project.web_url,
      default_branch: project.default_branch,
    }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to enable previews');
  }
  return resp.json();
}

export async function disconnectGitLab(orgSlug: string): Promise<void> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/gitlab/disconnect`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!resp.ok) throw new Error('Failed to disconnect GitLab');
}
